// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Uniform-grid broadphase over the static colliders.
//
// Static geometry is bucketed once at load and never moves — nudging displaces the ball's
// query position instead of the world, so the grid stays valid for the life of the
// machine. Six balls at 72 Hz work out at roughly 84 ball-steps per frame with 4–8
// candidates each: a few hundred analytic tests, which is why a 1000 Hz simulation is
// affordable rather than extravagant (PH-025).

import type { Collider } from './colliders'
import { colliderBounds } from './colliders'

/** Cell size as a multiple of the ball diameter. Four keeps buckets small without churn. */
const CELL_MULTIPLE = 4

export class Broadphase {
  private cellSize = 1
  private invCellSize = 1
  private minX = 0
  private minY = 0
  private cols = 1
  private rows = 1
  private cells: Int32Array[] = []

  /** Scratch result buffer, reused so a query allocates nothing. */
  readonly candidates = new Int32Array(256)
  private seen: Int32Array = new Int32Array(0)
  private seenStamp = 0
  private stamp = 0

  build(colliders: readonly Collider[], ballDiameter: number): void {
    this.cellSize = ballDiameter * CELL_MULTIPLE
    this.invCellSize = 1 / this.cellSize

    if (colliders.length === 0) {
      this.cols = 1
      this.rows = 1
      this.cells = [new Int32Array(0)]
      this.seen = new Int32Array(0)
      return
    }

    let minX = Infinity
    let minY = Infinity
    let maxX = -Infinity
    let maxY = -Infinity
    const bounds = colliders.map((c) => {
      const b = colliderBounds(c)
      if (b.minX < minX) minX = b.minX
      if (b.minY < minY) minY = b.minY
      if (b.maxX > maxX) maxX = b.maxX
      if (b.maxY > maxY) maxY = b.maxY
      return b
    })

    // A cell of padding so a ball just outside the geometry still finds its neighbours.
    this.minX = minX - this.cellSize
    this.minY = minY - this.cellSize
    this.cols = Math.max(1, Math.ceil((maxX + this.cellSize - this.minX) * this.invCellSize) + 1)
    this.rows = Math.max(1, Math.ceil((maxY + this.cellSize - this.minY) * this.invCellSize) + 1)

    const buckets: number[][] = Array.from({ length: this.cols * this.rows }, () => [])

    for (let i = 0; i < colliders.length; i++) {
      const b = bounds[i]!
      const c0 = this.clampCol(Math.floor((b.minX - this.minX) * this.invCellSize))
      const c1 = this.clampCol(Math.floor((b.maxX - this.minX) * this.invCellSize))
      const r0 = this.clampRow(Math.floor((b.minY - this.minY) * this.invCellSize))
      const r1 = this.clampRow(Math.floor((b.maxY - this.minY) * this.invCellSize))
      for (let r = r0; r <= r1; r++) {
        for (let c = c0; c <= c1; c++) {
          buckets[r * this.cols + c]!.push(i)
        }
      }
    }

    this.cells = buckets.map((b) => Int32Array.from(b))
    this.seen = new Int32Array(colliders.length)
    this.seenStamp = 0
    this.stamp = 0
  }

  private clampCol(c: number): number {
    return c < 0 ? 0 : c >= this.cols ? this.cols - 1 : c
  }

  private clampRow(r: number): number {
    return r < 0 ? 0 : r >= this.rows ? this.rows - 1 : r
  }

  /**
   * Collect the collider indices near a ball into `candidates`, **in ascending index order**.
   *
   * Duplicates across cells are removed with a stamp array rather than a Set, because a Set
   * allocates and this runs a thousand times a second.
   *
   * The ordering guarantee is PH-084 (M1-30) and it is not cosmetic. Cell traversal order
   * depends on the grid's origin and cell size, both of which are derived from the machine's
   * bounding box — so two runtimes that disagree by one ulp on a bound could visit the same
   * colliders in a different order, resolve two simultaneous contacts in a different order,
   * and diverge. Sorting by the collider's own index removes the grid from the answer
   * entirely. Buckets hold four to eight entries, where insertion sort is the fastest thing
   * available and allocates nothing.
   */
  query(x: number, y: number, radius: number): number {
    if (this.cells.length === 0) return 0

    this.stamp++
    // Wrap safely on the astronomically unlikely overflow rather than mis-deduplicating.
    if (this.stamp === 0x7fffffff) {
      this.seen.fill(0)
      this.stamp = 1
    }
    this.seenStamp = this.stamp

    const c0 = this.clampCol(Math.floor((x - radius - this.minX) * this.invCellSize))
    const c1 = this.clampCol(Math.floor((x + radius - this.minX) * this.invCellSize))
    const r0 = this.clampRow(Math.floor((y - radius - this.minY) * this.invCellSize))
    const r1 = this.clampRow(Math.floor((y + radius - this.minY) * this.invCellSize))

    let n = 0
    const cap = this.candidates.length
    for (let r = r0; r <= r1; r++) {
      const rowBase = r * this.cols
      for (let c = c0; c <= c1; c++) {
        const bucket = this.cells[rowBase + c]
        if (bucket === undefined) continue
        for (let k = 0; k < bucket.length; k++) {
          const idx = bucket[k]!
          if (this.seen[idx] === this.seenStamp) continue
          this.seen[idx] = this.seenStamp
          if (n < cap) this.candidates[n++] = idx
        }
      }
    }

    for (let i = 1; i < n; i++) {
      const value = this.candidates[i]!
      let j = i - 1
      while (j >= 0 && this.candidates[j]! > value) {
        this.candidates[j + 1] = this.candidates[j]!
        j--
      }
      this.candidates[j + 1] = value
    }
    return n
  }
}
